import { Bookmark, ExternalLink, Sparkles, Trash2 } from "lucide-react";
import { fmtRelative, api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useState } from "react";
import ExplainDrawer from "./ExplainDrawer";

const STATUS_COLOR = {
  active: "#10B981",
  archived: "#F59E0B",
  expired: "#EF4444",
};

function RankBar({ value }) {
  const v = Math.max(0, Math.min(100, Number(value || 0)));
  return (
    <span className="rank-bar" title={`${v.toFixed(0)}/100`}>
      <span style={{ width: `${v}%` }} />
    </span>
  );
}

function host(u) {
  try {
    return new URL(u).host.replace(/^www\./, "");
  } catch {
    return u;
  }
}

export default function IdeaRow({ idea, index }) {
  const { user } = useAuth();
  const [explain, setExplain] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!user) { toast.error("SIGN_IN_TO_SAVE"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/saves", {
        layer: "topic", ref_id: idea.id, topic_slug: idea.topic_slug,
        title: idea.title, text: idea.summary,
        payload: {
          source_1: idea.source_1, source_2: idea.source_2,
          source_3: idea.source_3 || null,
          source_count: idea.source_count || 2,
          idea_rank: idea.idea_rank, source_rank: idea.source_rank,
        },
      });
      setSaved(true);
      setSavedId(data?.id || null);
      toast.success("SAVED_TO_LIBRARY", { description: "Find it in My Library." });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "unknown error";
      toast.error("SAVE_FAILED", { description: msg });
    } finally {
      setBusy(false);
    }
  };

  const unsave = async () => {
    if (!savedId) return;
    setBusy(true);
    try {
      await api.delete(`/saves/${savedId}`);
      setSaved(false);
      setSavedId(null);
      toast.success("REMOVED_FROM_LIBRARY");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "unknown error";
      toast.error("DELETE_FAILED", { description: msg });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      data-testid="idea-card-item"
      className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-[#161616] hover:bg-[#0E0E0E] items-start"
    >
      {/* rank index */}
      <div className="col-span-1 font-mono text-[11px] text-zinc-500 tabnum pt-[2px]">
        #{String(index + 1).padStart(2, "0")}
      </div>

      {/* title + summary + sources */}
      <div className="col-span-7 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className="dot"
            style={{ background: STATUS_COLOR[idea.status] || "#71717A" }}
          />
          <span className="text-[10px] uppercase font-mono tracking-wider text-zinc-500">
            {idea.status}
          </span>
          <span className="text-[10px] uppercase font-mono tracking-wider text-zinc-600">
            · {idea.topic_slug}
          </span>
          <span className="ml-auto text-[10px] font-mono text-zinc-600 tabnum">
            {fmtRelative(idea.created_at)}
          </span>
          {saved ? (
            <button
              data-testid={`unsave-idea-${idea.id}`}
              onClick={unsave}
              disabled={busy}
              title="Remove from My Library"
              className="ml-2 inline-flex items-center gap-1 px-2 h-6 text-[10px] font-mono uppercase tracking-wider border border-rose-400/60 text-rose-300 hover:bg-rose-500/10 disabled:opacity-50"
            >
              <Trash2 size={10} /> REMOVE
            </button>
          ) : (
            <button
              data-testid={`save-idea-${idea.id}`}
              onClick={save}
              disabled={busy}
              title="Save to My Library"
              className="ml-2 inline-flex items-center gap-1 px-2 h-6 text-[10px] font-mono uppercase tracking-wider border border-purple-400/60 text-purple-300 hover:bg-purple-500/10 disabled:opacity-50"
            >
              <Bookmark size={10} /> {busy ? "…" : "SAVE"}
            </button>
          )}
          <button
            data-testid={`explain-idea-${idea.id}`}
            onClick={() => setExplain(true)}
            title="Why this rank?"
            className="ml-1 text-zinc-500 hover:text-amber-300 inline-flex items-center"
          >
            <Sparkles size={12} />
          </button>
        </div>
        <div className="mt-1 text-[14px] leading-snug text-white">
          {idea.title}
        </div>
        <div className="mt-1 text-[12px] leading-snug text-zinc-400">
          {idea.summary}
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 items-center text-[11px] font-mono">
          <a
            data-testid="idea-source-1-link"
            href={idea.source_1}
            target="_blank"
            rel="noopener noreferrer"
            className="src-link inline-flex items-center gap-1"
          >
            <span className="text-zinc-600">S1</span>
            <span className="truncate max-w-[220px]">{host(idea.source_1)}</span>
            <ExternalLink size={10} />
          </a>
          <a
            data-testid="idea-source-2-link"
            href={idea.source_2}
            target="_blank"
            rel="noopener noreferrer"
            className="src-link inline-flex items-center gap-1"
          >
            <span className="text-zinc-600">S2</span>
            <span className="truncate max-w-[220px]">{host(idea.source_2)}</span>
            <ExternalLink size={10} />
          </a>
          {idea.source_3 ? (
            <a
              data-testid="idea-source-3-link"
              href={idea.source_3}
              target="_blank"
              rel="noopener noreferrer"
              className="src-link inline-flex items-center gap-1"
            >
              <span className="text-zinc-600">S3</span>
              <span className="truncate max-w-[220px]">{host(idea.source_3)}</span>
              <ExternalLink size={10} />
            </a>
          ) : (
            <span
              data-testid="idea-under-sourced-badge"
              title="Under-sourced — only 2 independent sources attached (3 is the standard). Synthesis claims from this idea cap at [S1 ONLY]."
              className="text-[10px] uppercase tracking-wider text-amber-400/80 border border-amber-400/30 px-1.5 py-0.5"
            >
              sources 2/3
            </span>
          )}
        </div>
      </div>

      {/* ranks */}
      <div className="col-span-4 font-mono text-[11px] grid grid-cols-2 gap-x-3 gap-y-1 tabnum">
        <div className="flex items-center gap-2">
          <span className="text-zinc-500 w-10">IDEA</span>
          <RankBar value={idea.idea_rank} />
          <span className="w-9 text-right">{idea.idea_rank.toFixed(1)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-zinc-500 w-10">SRC</span>
          <RankBar value={idea.source_rank} />
          <span className="w-9 text-right">{idea.source_rank.toFixed(1)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-zinc-500 w-10">CRED</span>
          <RankBar value={idea.credibility_avg} />
          <span className="w-9 text-right">
            {idea.credibility_avg.toFixed(1)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-zinc-500 w-10">RECY</span>
          <RankBar value={idea.recency} />
          <span className="w-9 text-right">{idea.recency.toFixed(1)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-zinc-500 w-10">RELV</span>
          <RankBar value={idea.relevance} />
          <span className="w-9 text-right">{idea.relevance.toFixed(1)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-zinc-500 w-10">RPT</span>
          <RankBar value={idea.repeat_value} />
          <span className="w-9 text-right">{idea.repeat_value.toFixed(1)}</span>
        </div>
      </div>
      <ExplainDrawer
        open={explain}
        onClose={() => setExplain(false)}
        kind="idea"
        itemId={idea.id}
        title={idea.title}
      />
    </div>
  );
}
